import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Clock,
  Database,
  CheckCircle,
  AlertCircle,
  Play,
  Settings,
  FileSpreadsheet
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

export default function SystemSettings({ userProfile } = {}) {
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // CHA-289 — the Production Verification Google Sheet URL now lives here (admin home for config); the
  // Verification tab keeps a read-only link + Sync-now. Same admin-gated manage callable as CHA-282.
  // CHA-282 Codex P2 — platform roles must pass tenantId explicitly (resolveTenant requires it for them);
  // tenant admins send nothing and resolve to their own tenant. Mirrors VerificationContent.
  const isPlatformRole = userProfile?.role === 'platform_owner' || userProfile?.role === 'platform_admin';
  const tenantArg = isPlatformRole ? { tenantId: userProfile?.tenantId || 'inmocean' } : {};
  const [sheetCfg, setSheetCfg] = useState(null);          // { sheetUrl, spreadsheetId } — server-sanitized
  const [sheetUrlInput, setSheetUrlInput] = useState('');
  const [sheetSaving, setSheetSaving] = useState(false);
  const [sheetResult, setSheetResult] = useState(null);    // { success, message }
  const [sheetCardHidden, setSheetCardHidden] = useState(false); // non-admin: config read denied → hide
  // CHA-289 review round (CodeRabbit) — a Save that completes BEFORE the initial get() resolves must not be
  // clobbered by the late get response (stale pre-save config). Any user write flips this latch; the initial
  // loader then drops its result.
  const userDirtyRef = React.useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const manageCfg = httpsCallable(getFunctions(), 'manageProductionVerificationConfig');
        const res = await manageCfg({ op: 'get', ...tenantArg });
        if (!alive || userDirtyRef.current) return; // stale pre-save snapshot — the user's save wins
        setSheetCfg(res.data || null);
        setSheetUrlInput(res.data?.sheetUrl || '');
      } catch (err) {
        if (!alive) return;
        // Non-admins can't read the admin-gated config — hide the card instead of pinning an error
        // on a control they can never use (same §6.3 pattern as VerificationContent).
        if (err?.code === 'functions/permission-denied') setSheetCardHidden(true);
        else setSheetResult({ success: false, message: err.message });
      }
    })();
    return () => { alive = false; };
    // tenantArg is derived from userProfile (stable per mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveSheetUrl = async () => {
    userDirtyRef.current = true; // from here on, the initial loader's late response is stale
    setSheetSaving(true);
    setSheetResult(null);
    try {
      const manageCfg = httpsCallable(getFunctions(), 'manageProductionVerificationConfig');
      const res = await manageCfg({ op: 'set', sheetUrl: sheetUrlInput.trim(), ...tenantArg });
      // Always show the SERVER-sanitized stored URL (rebuilt from the extracted id) — never the raw input.
      setSheetCfg(res.data || null);
      setSheetUrlInput(res.data?.sheetUrl || '');
      setSheetResult({ success: true, message: res.data?.spreadsheetId ? 'Sheet URL saved.' : 'Sheet URL cleared.' });
    } catch (err) {
      setSheetResult({ success: false, message: err.message });
    } finally {
      setSheetSaving(false);
    }
  };

  // Subscribe to order_base refresh status from Firestore
  useEffect(() => {
    const db = getFirestore();
    const statusRef = doc(db, 'system_status', 'order_base_refresh');

    const unsubscribe = onSnapshot(statusRef, (docSnap) => {
      if (docSnap.exists()) {
        setRefreshStatus(docSnap.data());
      }
      setLoading(false);
    }, (err) => {
      console.error('Error listening to refresh status:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setRefreshResult(null);

    try {
      const functions = getFunctions();
      const refreshOrderBase = httpsCallable(functions, 'refreshOrderBase');
      const result = await refreshOrderBase();

      setRefreshResult({
        success: true,
        message: `Successfully refreshed order_base table with ${result.data.rowCount?.toLocaleString() || 'N/A'} rows`,
        duration: result.data.durationSeconds
      });
    } catch (err) {
      console.error('Error refreshing order_base:', err);
      setRefreshResult({
        success: false,
        message: err.message || 'Failed to refresh order_base table'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeSinceRefresh = () => {
    if (!refreshStatus?.last_refreshed) return null;
    const lastRefresh = refreshStatus.last_refreshed.toDate ?
      refreshStatus.last_refreshed.toDate() :
      new Date(refreshStatus.last_refreshed);
    const now = new Date();
    const diffMs = now - lastRefresh;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${Math.floor(diffHours / 24)} days ago`;
  };

  return (
    <div className="space-y-6 p-6">
      {/* CHA-289 — Production Verification Google Sheet (URL config home; sync stays in Production → Verification) */}
      {!sheetCardHidden && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="text-blue-600" size={24} />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Production Verification — Google Sheet</h2>
                <p className="text-sm text-gray-600">
                  The dedicated sheet the verification checks sync into (Costing Errors, Stale Issued, Stale Shipped, Term Rule Errors).
                  Run the sync from Production → Verification.
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {sheetCfg?.sheetUrl ? (
              <div className="text-sm text-gray-600">
                Current sheet:{' '}
                <a href={sheetCfg.sheetUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 underline break-all">
                  {sheetCfg.sheetUrl}
                </a>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No sheet configured yet.</div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="text"
                placeholder="https://docs.google.com/spreadsheets/d/…"
                value={sheetUrlInput}
                onChange={(e) => setSheetUrlInput(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-96 max-w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={handleSaveSheetUrl}
                disabled={sheetSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {sheetSaving ? 'Saving…' : 'Save'}
              </button>
              <span className="text-sm text-gray-500">Paste a Google Sheets URL (or a bare spreadsheet id). Save an empty field to clear.</span>
            </div>
            {sheetResult && (
              <div className={`flex items-start gap-3 p-4 rounded-lg ${
                sheetResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {sheetResult.success ? (
                  <CheckCircle className="text-green-500 mt-0.5" size={18} />
                ) : (
                  <AlertCircle className="text-red-500 mt-0.5" size={18} />
                )}
                <p className={`text-sm ${sheetResult.success ? 'text-green-700' : 'text-red-700'}`}>{sheetResult.message}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Base Materialized Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Database className="text-blue-600" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Order Base Data Cache</h2>
              <p className="text-sm text-gray-600">
                Pre-computed table for Sales page (Pick & Open orders with routing data)
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Last Refresh */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Clock size={16} />
                <span className="text-sm font-medium">Last Refreshed</span>
              </div>
              {loading ? (
                <div className="animate-pulse h-5 bg-gray-200 rounded w-32"></div>
              ) : (
                <>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(refreshStatus?.last_refreshed)}
                  </p>
                  {refreshStatus?.last_refreshed && (
                    <p className="text-sm text-gray-500 mt-1">
                      {getTimeSinceRefresh()}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Row Count */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Database size={16} />
                <span className="text-sm font-medium">Row Count</span>
              </div>
              {loading ? (
                <div className="animate-pulse h-5 bg-gray-200 rounded w-24"></div>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  {refreshStatus?.row_count?.toLocaleString() || 'N/A'}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Settings size={16} />
                <span className="text-sm font-medium">Status</span>
              </div>
              {loading ? (
                <div className="animate-pulse h-5 bg-gray-200 rounded w-20"></div>
              ) : (
                <div className="flex items-center gap-2">
                  {refreshStatus?.status === 'success' ? (
                    <>
                      <CheckCircle className="text-green-500" size={18} />
                      <span className="text-lg font-semibold text-green-600">Healthy</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="text-amber-500" size={18} />
                      <span className="text-lg font-semibold text-amber-600">
                        {refreshStatus?.status || 'Unknown'}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Duration from last refresh */}
          {refreshStatus?.duration_seconds && (
            <div className="text-sm text-gray-500">
              Last refresh took {refreshStatus.duration_seconds.toFixed(1)} seconds
            </div>
          )}

          {/* Manual Refresh Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isRefreshing ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Refreshing...
                </>
              ) : (
                <>
                  <Play size={18} />
                  Refresh Now
                </>
              )}
            </button>

            <span className="text-sm text-gray-500">
              Manually trigger a refresh of the order_base cache
            </span>
          </div>

          {/* Refresh Result Message */}
          {refreshResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg ${
              refreshResult.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              {refreshResult.success ? (
                <CheckCircle className="text-green-500 mt-0.5" size={18} />
              ) : (
                <AlertCircle className="text-red-500 mt-0.5" size={18} />
              )}
              <div>
                <p className={`font-medium ${refreshResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {refreshResult.success ? 'Refresh Completed' : 'Refresh Failed'}
                </p>
                <p className={`text-sm ${refreshResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {refreshResult.message}
                </p>
                {refreshResult.duration && (
                  <p className="text-sm text-gray-500 mt-1">
                    Completed in {refreshResult.duration.toFixed(1)} seconds
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Schedule Info */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Automatic Refresh Schedule</h3>
            <p className="text-sm text-gray-500">
              Currently, the order_base table can be refreshed manually.
              Scheduled automatic refreshes can be configured using Firebase scheduled functions.
            </p>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Run a manual refresh whenever the underlying rabi_invoices
                or routing_pick_status data is updated to ensure the Sales page shows the latest data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
